import { Navigate, Route, Routes, useLocation, useParams } from 'react-router';
import { RedirectIfAuthenticated, RequireAuth } from './auth-guard';
import { AppLayout } from './layout/AppLayout';
import { AuthLayout } from './layout/AuthLayout';
import { CompraSucessoPage } from './pages/CompraSucessoPage';
import { destinoDoRetorno } from './pages/retorno';
import { EstudarPage } from './pages/EstudarPage';
import { LegislacaoPage } from './pages/LegislacaoPage';
import { HomePage } from './pages/HomePage';
import { ConfiguracoesLayout } from './pages/configuracoes/ConfiguracoesLayout';
import { ContaPage } from './pages/configuracoes/ContaPage';
import { DesempenhoPage } from './pages/configuracoes/DesempenhoPage';
import { PlanoPage } from './pages/configuracoes/PlanoPage';
import { CriarContaPage } from './pages/auth/CriarContaPage';
import { EsqueciSenhaPage } from './pages/auth/EsqueciSenhaPage';
import { LoginPage } from './pages/auth/LoginPage';
import { NovaSenhaPage } from './pages/auth/NovaSenhaPage';
import { VerifiqueOEmailPage } from './pages/auth/VerifiqueOEmailPage';

/**
 * Rotas do app do aluno. A escolha do módulo é a porta de entrada; o estudo
 * acontece em `/estudar`, com o módulo no endereço para que a tela seja
 * compartilhável e o botão de voltar funcione.
 *
 * O que é da pessoa - conta, desempenho, plano - fica sob `/configuracoes`,
 * cada assunto com endereço próprio.
 */
export function App() {
  return (
    <Routes>
      <Route element={<RedirectIfAuthenticated />}>
        <Route element={<AuthLayout />}>
          <Route path="/entrar" element={<LoginPage />} />
          <Route path="/criar-conta" element={<CriarContaPage />} />
          <Route path="/esqueci-a-senha" element={<EsqueciSenhaPage />} />
        </Route>
      </Route>

      {/* A nova senha é acessível mesmo com sessão ativa: o link chega por
          e-mail e pode ser aberto em qualquer estado. A espera pela confirmação
          do e-mail fica ao lado dela e pelo mesmo motivo: é instrução, e
          instrução não pode ser interrompida por um guarda. */}
      <Route element={<AuthLayout />}>
        <Route path="/nova-senha" element={<NovaSenhaPage />} />
        <Route path="/verifique-o-email" element={<VerifiqueOEmailPage />} />
      </Route>

      <Route element={<RequireAuth />}>
        {/*
          A compra concluída fica **fora do `AppLayout`**, e é a única tela do
          aluno que fica: é um momento, não um lugar do app. Com a barra lateral
          em volta ela voltaria a parecer uma página de configurações, que é
          exatamente o que ela deixou de ser (D-3).
        */}
        <Route path="/compra/sucesso" element={<CompraSucessoPage />} />

        <Route element={<AppLayout />}>
          <Route index element={<HomePage />} />
          <Route path="/estudar" element={<EstudarPage />} />
          <Route path="/legislacao" element={<LegislacaoPage />} />

          <Route path="/configuracoes" element={<ConfiguracoesLayout />}>
            <Route index element={<Navigate to="conta" replace />} />
            <Route path="conta" element={<ContaPage />} />
            <Route path="desempenho" element={<DesempenhoPage />} />
            {/* O provedor devolve o comprador em /plano/sucesso, /cancelado ou
                /expirado. Quem voltou **sem** ter pago continua aqui, porque a
                pergunta dele é "o que eu tenho?" e a resposta é esta tela; quem
                pagou vai para a tela da compra concluída, que é outra coisa. */}
            <Route path="plano" element={<PlanoPage />} />
            <Route path="plano/:retorno" element={<RetornoDoPagamento />} />

            {/*
              Os endereços de Configurações que existiram até 05/09/2026, quando
              "Assinatura" e "Planos" viraram "Plano".

              **A `assinatura` não é só cortesia com quem tem link antigo:** uma
              cobrança Pix em aberto carrega a URL de retorno gravada no dia em
              que foi gerada, e ela aponta para `/configuracoes/assinatura/sucesso`.
              Tirar o redirecionamento faria quem pagou um boleto de ontem cair
              numa tela em branco hoje - o mesmo motivo que preservou o `/assinatura`
              da raiz em 02/09.
            */}
            <Route path="assinatura" element={<Navigate to="/configuracoes/plano" replace />} />
            <Route path="assinatura/:retorno" element={<RetornoAntigo />} />
            <Route path="planos" element={<Navigate to="/configuracoes/plano" replace />} />
          </Route>

          {/*
            Os endereços antigos, que existiram até 02/09/2026.
            Não são só marcadores de histórico: **uma cobrança Pix em aberto
            carrega a URL de retorno gravada no dia em que foi gerada**, e ela
            aponta para `/assinatura/sucesso`. Tirar o redirecionamento faria
            quem pagou um boleto de ontem cair numa tela em branco hoje.
          */}
          <Route path="/assinatura/*" element={<Redirecionar para="/configuracoes/plano" />} />
          <Route path="/planos" element={<Navigate to="/configuracoes/plano" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}

/**
 * Redireciona preservando o resto do caminho e a query string.
 *
 * O `Navigate` sozinho jogaria fora `?pedido=...`, que é o identificador que a
 * tela de Plano manda para o analytics ao confirmar a compra.
 */
function Redirecionar({ para }: { para: string }) {
  const location = useLocation();
  const resto = location.pathname.replace(/^\/(assinatura|planos)/, '');
  return <Navigate to={`${para}${resto}${location.search}`} replace />;
}

/**
 * `/configuracoes/assinatura/sucesso` e irmãos, que cobranças em aberto ainda
 * carregam, viram `/configuracoes/plano/sucesso` - com o `?pedido=` intacto.
 */
function RetornoAntigo() {
  const location = useLocation();
  const { retorno } = useParams<{ retorno?: string }>();
  return <Navigate to={`/configuracoes/plano/${retorno}${location.search}`} replace />;
}

/**
 * A volta do provedor, que desde 17/09/2026 tem dois destinos.
 *
 * **`/configuracoes/plano/sucesso` não pode sumir** (G-RETURN): a URL de retorno
 * viaja gravada dentro da cobrança, e um Pix gerado hoje volta por ela semanas
 * depois. Ela redireciona para a tela da compra concluída levando o
 * `?pedido=`, que é o que essa tela usa para achar o recibo.
 *
 * Cancelado e expirado continuam na tela de Plano: quem não pagou volta com a
 * pergunta "o que eu tenho?", e é lá que a oferta reabre.
 */
function RetornoDoPagamento() {
  const location = useLocation();
  const { retorno } = useParams<{ retorno?: string }>();
  const destino = destinoDoRetorno(retorno, location.search);

  return destino ? <Navigate to={destino} replace /> : <PlanoPage />;
}
